/**
 * Template do cardápio do cliente (0084, proposta menu-feed): escolha entre
 * a lista clássica e a Grade 2× (fotos grandes, carrossel por produto e
 * filtros de alergias/ingredientes). Os esboços usam as cores atuais do
 * Branding para dar a noção do resultado.
 */
import { useState } from 'react'
import { useMutation, type QueryClient } from '@tanstack/react-query'
import { config, type RestaurantConfig } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Card, Button, toast } from './ui/index.js'
import { cn } from '../lib/utils.js'

export function TemplateForm({ cfg, qc }: { cfg: RestaurantConfig; qc: QueryClient }) {
  const { t } = useI18n()
  const [template, setTemplate] = useState<'list' | 'grid' | 'feed_1'>(cfg.menu_template ?? 'list')
  const dirty = template !== (cfg.menu_template ?? 'list')

  const mut = useMutation({
    mutationFn: (body: { menu_template: 'list' | 'grid' | 'feed_1' }) => config.update(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.saved_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const options = [
    {
      value: 'list' as const,
      title: t('admin.branding.menu_template_list'),
      desc: t('admin.branding.menu_template_list_desc'),
      sketch: <MenuSketchList cfg={cfg} />,
    },
    {
      value: 'grid' as const,
      title: t('admin.branding.menu_template_grid'),
      desc: t('admin.branding.menu_template_grid_desc'),
      sketch: <MenuSketchGrid cfg={cfg} />,
    },
    {
      value: 'feed_1' as const,
      title: t('admin.branding.menu_template_feed1'),
      desc: t('admin.branding.menu_template_feed1_desc'),
      sketch: <MenuSketchFeed cfg={cfg} />,
    },
  ]

  return (
    <Card
      title={t('admin.branding.menu_template')}
      description={t('admin.branding.menu_template_desc')}
    >
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        role="radiogroup"
        aria-label={t('admin.branding.menu_template')}
      >
        {options.map((opt) => {
          const selected = template === opt.value
          return (
            <button
              type="button"
              key={opt.value}
              role="radio"
              aria-checked={selected}
              onClick={() => setTemplate(opt.value)}
              className={cn(
                'text-left rounded-xl border-2 p-3 transition-colors cursor-pointer',
                selected
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white',
              )}
            >
              <div
                className="rounded-lg border border-gray-200 overflow-hidden mb-2"
                style={{ background: cfg.background_color }}
              >
                {opt.sketch}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'w-4 h-4 rounded-full border-2 inline-block',
                    selected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300',
                  )}
                  aria-hidden="true"
                />
                <span className="text-sm font-semibold text-gray-900">{opt.title}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
            </button>
          )
        })}
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          disabled={!dirty || mut.isPending}
          onClick={() => mut.mutate({ menu_template: template })}
        >
          {t('admin.common.save')}
        </Button>
      </div>
    </Card>
  )
}

/** Esboço da lista clássica: linhas com foto pequena à direita. */
function MenuSketchList({ cfg }: { cfg: RestaurantConfig }) {
  const ink = cfg.secondary_color
  return (
    <div className="p-2 space-y-2" aria-hidden="true">
      <div className="flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="text-[7px] font-bold px-1.5 py-0.5 rounded"
            style={{
              color: ink,
              border: `1px solid ${ink}`,
              background: i === 0 ? ink : 'transparent',
            }}
          >
            {'···'}
          </span>
        ))}
      </div>
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1 space-y-1">
            <div
              className="h-1.5 rounded"
              style={{ background: ink, opacity: 0.75, width: '70%' }}
            />
            <div className="h-1 rounded" style={{ background: ink, opacity: 0.35, width: '90%' }} />
          </div>
          <div
            className="w-8 h-6 rounded shrink-0"
            style={{ background: cfg.primary_color, opacity: 0.8 }}
          />
        </div>
      ))}
    </div>
  )
}

/** Esboço do feed 1×1: um post grande por linha com descrição. */
function MenuSketchFeed({ cfg }: { cfg: RestaurantConfig }) {
  const ink = cfg.secondary_color
  return (
    <div className="p-2 space-y-2" aria-hidden="true">
      <div className="flex justify-between items-center">
        <span
          className="text-[7px] font-bold px-1.5 py-0.5 rounded"
          style={{ color: ink, border: `1px solid ${ink}` }}
        >
          {'···'}
        </span>
        <span className="text-[7px] font-bold" style={{ color: ink }}>
          12,00
        </span>
      </div>
      <div
        className="aspect-[4/3] rounded"
        style={{ background: cfg.primary_color, opacity: 0.85 }}
      />
      <div className="space-y-1">
        <div className="h-1 rounded" style={{ background: ink, opacity: 0.7, width: '60%' }} />
        <div className="h-1 rounded" style={{ background: ink, opacity: 0.35, width: '85%' }} />
      </div>
    </div>
  )
}

/** Esboço da grade 2×: quadrados grandes de foto lado a lado. */
function MenuSketchGrid({ cfg }: { cfg: RestaurantConfig }) {
  const ink = cfg.secondary_color
  return (
    <div className="p-2" aria-hidden="true">
      <div className="flex justify-between items-center mb-1.5">
        {[0, 1].map((i) => (
          <span
            key={i}
            className="text-[7px] font-bold px-1.5 py-0.5 rounded"
            style={{
              color: ink,
              border: `1px solid ${ink}`,
              background: i === 0 ? ink : 'transparent',
            }}
          >
            {'···'}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <div
              className="aspect-square rounded"
              style={{ background: cfg.primary_color, opacity: i === 3 ? 0.5 : 0.85 }}
            />
            <div
              className="h-1 rounded mt-1"
              style={{ background: ink, opacity: 0.6, width: '80%' }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
